import { Box } from "@mui/system";
import React, { useEffect, useState } from "react";
import { ActivitiesBarComponent } from "../../../shared/components/activities-bar/activities-bar.component";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  IconButton,
} from "@mui/material";
import FlagIcon from "@mui/icons-material/Flag";
import StarIcon from "@mui/icons-material/Star";
import { useGetAdsHook } from "../hooks/ads.hook";
import { useEventsStore } from "../../../shared/stores/events.store";
import { HosterAdsDetailsComponent } from "../../hosters/components/hoster-ads-details.component";
import { PagerComponent } from "../../../shared/elements/pager.component";
import { DEFAULT_EVENT_IMAGE } from "../../../shared/constants/default-image";

export interface AdsProps {
  // Define any props needed for the ads component
}

export const AdsComponent: React.FC<AdsProps> = () => {
  const { event } = useEventsStore();
  const [page, setPage] = useState(1);
  const { data: ads, isLoading } = useGetAdsHook(event, page);
  const [selectedAd, setSelectedAd] = useState<{
    id: string;
    title: string;
  } | null>(null);

  useEffect(() => {
    setPage(1);
  }, [event]);

  if (selectedAd) {
    return (
      <HosterAdsDetailsComponent
        id={selectedAd.id}
        activityName={selectedAd.title}
        onBack={() => setSelectedAd(null)}
      />
    );
  }

  return (
    <Box p={1.5} sx={{ overflowY: "auto", height: "calc(100vh - 48px)" }}>
      <ActivitiesBarComponent />
      <Box mt={2}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
            gap: 2,
          }}
        >
          {ads && ads.data.length > 0
            ? ads.data.map((activity) => (
                <Card
                  key={activity.id}
                  onClick={() =>
                    setSelectedAd({
                      id: activity.id.toString(),
                      title: activity.title,
                    })
                  }
                  sx={{
                    borderRadius: "12px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                    },
                    position: "relative",
                  }}
                >
                  <Box sx={{ position: "relative" }}>
                    <CardMedia
                      component="img"
                      height="160"
                      image={activity.images[0]?.image || DEFAULT_EVENT_IMAGE}
                      alt={activity.title}
                      sx={{
                        borderTopLeftRadius: "12px",
                        borderTopRightRadius: "12px",
                      }}
                    />
                    <IconButton
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 1)",
                        },
                      }}
                    >
                      <FlagIcon sx={{ color: "#B4006E" }} />
                    </IconButton>
                  </Box>
                  <CardContent sx={{ padding: "12px" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mb: 0.5,
                      }}
                    >
                      <StarIcon sx={{ color: "#FFD700", fontSize: 16 }} />
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: "#333", fontSize: "0.8rem" }}
                      >
                        4.9
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#666", fontSize: "0.75rem" }}>
                        (120 avis)
                      </Typography>
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        fontSize: "14px",
                        mb: 0.5,
                        color: "#333",
                      }}
                    >
                      {activity.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#666", mb: 1, fontSize: "0.78rem" }}>
                      {activity.city
                        ? `${activity.city}, ${activity.country}`
                        : `${activity.meetCity}, ${activity.meetCountry}`}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#B4006E",
                        fontWeight: 600,
                        fontSize: "12px",
                      }}
                    >
                      {Number(activity.price) > 0
                        ? `À partir de ${activity.price}€ / personne`
                        : "Voir le menu"}
                    </Typography>
                  </CardContent>
                </Card>
              ))
            : !isLoading && (
                <Typography variant="body2">Aucune activité trouvée</Typography>
              )}
        </Box>
        {ads && (
          <PagerComponent
            page={ads.page}
            totalPages={ads.totalPages}
            total={ads.total}
            onPageChange={setPage}
          />
        )}
      </Box>
    </Box>
  );
};
